/**
 * /settings/publisher-api — FS-226 alias route that re-mounts the
 * publisher-facing API console under the `/settings` namespace so admins can
 * reach the v2 publisher-scoped console from the tenant-settings menu.
 *
 * The route delegates rendering to `PublisherApiPage`, which already hosts
 * both the FS-142 affiliate console AND the FS-226 publisher_id-scoped
 * console, one above the other. Using the shared component keeps behaviour
 * identical between `/publishers/api` and `/settings/publisher-api`.
 *
 * Note: this file is registered inside its own owned-files lane; the root
 * router wire-up (router.tsx) is FORBIDDEN for this agent and is expected
 * to pick this route up in a follow-up commit.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { PublisherApiPage } from "@/features/publisher-api/PublisherApiPage";

// ---------------------------------------------------------------------------
// Search params — optional `publisherId` query so deep-links can pre-scope
// the v2 console to one publisher on landing.
// ---------------------------------------------------------------------------

interface SettingsPublisherApiSearch {
	publisherId?: string;
}

export const settingsPublisherApiRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/settings/publisher-api",
	validateSearch: (raw: Record<string, unknown>): SettingsPublisherApiSearch => {
		const v = raw.publisherId;
		if (typeof v === "string" && v.trim().length > 0) {
			return { publisherId: v.trim() };
		}
		return {};
	},
	component: PublisherApiPage,
});

export default settingsPublisherApiRoute;

// Re-export so dashboards can import either the route or the page component
// from a single module path, mirroring publishers.api.tsx.
export { PublisherApiPage } from "@/features/publisher-api/PublisherApiPage";
